import { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ShieldCheck, MessageCircle, Pencil, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

function IntroEdit({ value, onSaved, onCancel }) {
  const [t, setT] = useState(value);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/staff/intro", { intro: t }); onSaved(data.intro); toast.success("Steckbrief gespeichert 💖"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <span className="mt-2 block space-y-1.5">
      <textarea autoFocus rows={2} maxLength={140} value={t} onChange={(e) => setT(e.target.value)} placeholder="Ein kurzer Satz über dich …"
        className="w-full rounded-2xl border border-pink-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="team-intro-input" />
      <span className="flex items-center gap-2">
        <span className="text-[11px] text-slate-400 flex-1">{t.length}/140</span>
        <button type="button" onClick={onCancel} className="h-8 px-3 rounded-full text-xs font-bold text-slate-500 hover:bg-slate-100" data-testid="team-intro-cancel">Abbrechen</button>
        <button type="button" onClick={save} disabled={busy} className="h-8 px-3 rounded-full pink-gradient text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60" data-testid="team-intro-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Speichern
        </button>
      </span>
    </span>
  );
}

export function TeamPerson({ p, lead, i, me }) {
  const [intro, setIntro] = useState(p.intro);
  const [edit, setEdit] = useState(false);
  const mine = p.user_id === me;
  return (
    <div className="lux-card rounded-3xl p-4 fade-up" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`team-${lead ? "lead" : "mod"}-${p.user_id}`}>
      <div className="flex items-start gap-4">
        <Link to={`/u/${p.user_id}`} className={`shrink-0 rounded-full p-[3px] ${lead ? "bg-gradient-to-br from-amber-200 to-amber-500" : "bg-gradient-to-br from-sky-300 to-blue-500"}`} data-testid={`team-avatar-${p.user_id}`}>
          <UserAvatar userId={p.user_id} name={p.name} picture={p.picture} size={lead ? "w-16 h-16" : "w-12 h-12"} noStory />
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={`/u/${p.user_id}`} className="block font-display font-extrabold text-slate-800 truncate hover:text-rose-600" data-testid={`team-name-${p.user_id}`}>{p.name}</Link>
          <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-extrabold ${lead ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-700"}`}>
            {lead ? <Crown className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />} {p.title}
          </span>
          {edit ? <IntroEdit value={intro} onSaved={(v) => { setIntro(v); setEdit(false); }} onCancel={() => setEdit(false)} />
            : intro ? <p className="mt-1.5 text-sm text-slate-600 italic leading-snug" data-testid={`team-intro-${p.user_id}`}>„{intro}“</p>
            : mine ? <p className="mt-1.5 text-xs text-slate-400">Noch kein Steckbrief – schreib einen Satz über dich!</p> : null}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        {mine && !edit && <button type="button" onClick={() => setEdit(true)} className="h-9 px-3.5 rounded-full bg-rose-50 text-rose-600 text-xs font-bold inline-flex items-center gap-1.5" data-testid="team-intro-edit"><Pencil className="w-3.5 h-3.5" /> Steckbrief {intro ? "bearbeiten" : "schreiben"}</button>}
        <Link to={`/u/${p.user_id}`} className="h-9 px-3.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold inline-flex items-center" data-testid={`team-profile-${p.user_id}`}>Profil</Link>
        {!mine && <Link to={`/nachrichten/${p.user_id}`} className="h-9 px-3.5 rounded-full pink-gradient text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid={`team-message-${p.user_id}`}><MessageCircle className="w-3.5 h-3.5" /> Schreiben</Link>}
      </div>
    </div>
  );
}
